import config from 'src/AppConfig.js';
import Logger from 'src/utils/logger';
import * as Sentry from '@sentry/browser';
import { isPast } from 'date-fns';

const getCookie = (name) => {
  const cookie = document.cookie.match(`(^|;) ?${name}=([^;]*)(;|$)`);
  return cookie ? cookie[2] : null;
};

const setHeaders = () => {
  const cookie = getCookie('SoleAuth');
  const headers = {};
  if (cookie) {
    headers.SoleAuth = `SoleAuth=${cookie}`;
  }
  return headers;
};

const defaultOptions = {
  mode: 'cors',
  headers: setHeaders(),
};

const makeCall = async (url, options = defaultOptions) => {
  return fetch(url, options).then((response) => response);
};

const getUserId = async () => {
  try {
    Logger('debug', 'checking if user is registered...');
    const response = await makeCall(
      `${process.env.API_ENDPOINT}${config.checkUrl}`,
    );
    const payload = await response.json();
    switch (response.status) {
      case 200:
        Logger('debug', `user accepted with uid: ${payload.uid}`);
        return payload.uid;
      case 404:
        // Caso utente non registrato
        Sentry.addBreadcrumb({
          category: 'auth',
          message: `user not registered.`,
          level: 'warning',
        });
        throw new Error('UserNotRegistered');
      case 500:
        // Caso utente non registrato
        Sentry.addBreadcrumb({
          category: 'auth',
          message: `Invalid SoleAuth cookie or expired`,
          level: 'error',
        });
        throw new Error('BadSoleAuthException');
      case 400:
        // Caso cooke non trovato
        Sentry.addBreadcrumb({
          category: 'auth',
          message: `Cookie sole not found`,
          level: 'error',
        });
        throw new Error('CookieNotFound');
      default:
        Sentry.addBreadcrumb({
          category: 'auth',
          data: {
            ...payload,
          },
          level: 'error',
        });
        throw new Error('Errore generico getUserId');
    }
  } catch (err) {
    Logger('error', 'errore chiamata isUserRegistered');
    Logger('error', err);
    if (
      !['BadSoleAuthException', 'UserNotRegistered', 'CookieNotFound'].includes(
        err.message,
      )
    ) {
      Sentry.captureException(err);
    }
    return false;
  }
};

const sendBeatToAnalytics = async (userId, endEvent) => {
  try {
    if (document.visibilityState === 'hidden') {
      Logger('debug', 'the page is not visible, abort beat...');
      return;
    }
    // Ulteriore controllo sulla data di fine evento, se l'evento è terminato, non faccio nulla
    if (isPast(endEvent)) {
      Logger('debug', 'Event is not live, abort beat...');
      return;
    }
    Logger('debug', 'sending beat...');
    await makeCall(`${process.env.API_ENDPOINT}${config.sendBeat}`, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        uid: userId,
        type: 'beat',
      }),
    });
    const beatDate = new Date().toISOString();
    Sentry.addBreadcrumb({
      category: 'analytics',
      message: `Beat sent at ${beatDate}`,
      level: 'info',
    });
    Logger('debug', `beat sent at ${beatDate}`);
    return true;
  } catch (err) {
    Logger('error', 'errore chiamata sendBeatToAnalytics');
    Logger('error', err);
    return false;
  }
};

const confirmPresence = async (userId, vid) => {
  try {
    Logger('debug', `waiting user ${userId} to confirm check ${vid}`);
    await makeCall(`${process.env.API_ENDPOINT}${config.sendVerify}`, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        uid: userId,
        vid,
      }),
    });
    Sentry.addBreadcrumb({
      category: 'analytics',
      message: `Confirmed presence at check ${vid}`,
      level: 'info',
    });
    Logger('debug', `check ${vid} confirmed`);
    return true;
  } catch (err) {
    Logger('error', 'errore chiamata confirmPresence');
    Logger('error', err);
    Sentry.captureException(err);
    return false;
  }
};

export default { getUserId, sendBeatToAnalytics, confirmPresence };
